import axios from "axios";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ebimbingan from "../../asset/ebimbingan.png";
import { EyeIcon, EyeOffIcon } from "@heroicons/react/solid";

toast.configure();

const POPULAR_EMAIL_DOMAINS: string[] = [
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "icloud.com",
];

export function Register() {
  const [loading, setLoading] = useState(false);
  const email = useFormInput("");
  let navigate = useNavigate();
  const [passwordReg, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [UsernamedReg, setUsername] = useState("");
  const [error, setError] = useState<string | null | boolean>(null);
  const [usernameIsValid, setUsernameIsValid] = useState(true);
  const [usernameError, setUsernameError] = useState(
    "Mestilah antara 4 hingga 18 karakter dan tiada ruang"
  );
  const [emailIsValid, setEmailIsValid] = useState(true);
  const [emailError, setEmailError] = useState("Sila masukkan emel yang sah.");
  const [emailSuggestions, setEmailSuggestions] = useState<string[]>([]);
  const [showEmailSuggestions, setShowEmailSuggestions] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);
  const [registeredJwt, setRegisteredJwt] = useState<string | null>(null);
  const [passwordReq1, setPasswordReq1] = useState({
    state: true,
    error: "Melebihi lapan (8) karakter",
  });
  const [passwordReq2, setPasswordReq2] = useState({
    state: true,
    error: "Kombinasi huruf kecil & huruf besar",
  });
  const [passwordReq3, setPasswordReq3] = useState({
    state: true,
    error: "Sekurang-kurangnya satu (1) karakter istimewa",
  });
  const [passwordReq4, setPasswordReq4] = useState({
    state: true,
    error: "Sekurang-kurangnya satu (1) nombor",
  });

  const requestVerification = async (
    targetEmail: string,
    jwtToken?: string | null
  ) => {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/email/request-verification`,
      {
        email: targetEmail,
      },
      {
        headers: {
          ...(jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {}),
          "Content-Type": "application/json",
        },
      }
    );

    if (!res?.data?.ok) {
      throw new Error(
        res?.data?.error?.message || "Tidak dapat menghantar emel pengesahan."
      );
    }
  };

  const validateEmail = (val: string) => {
    // Simple RFC-5322-like email validation
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return regex.test(val);
  };

  const handleRegistration = () => {
    // Username validation (unchanged rule)
    const usernameValid =
      /^[a-zA-Z0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>\/?]{4,18}$/.test(
        UsernamedReg
      );
    setUsernameIsValid(usernameValid);
    setUsernameError(
      usernameValid
        ? ""
        : "Mestilah antara 4 hingga 18 karakter dan tiada ruang"
    );

    // Email validation
    const emailValid = validateEmail(email.value.trim());
    setEmailIsValid(emailValid);
    setEmailError(emailValid ? "" : "Sila masukkan emel yang sah.");

    // Password validations (unchanged rules)
    const hasUpperCase = /[A-Z]/.test(passwordReg);
    const hasLowerCase = /[a-z]/.test(passwordReg);
    const hasNumbers = /\d/.test(passwordReg);
    const hasSpecialChars = /[!@#$%^&*(),.?":{}|<>]/.test(passwordReg);
    const isLengthValid = passwordReg.length >= 8;

    setPasswordReq1({ ...passwordReq1, state: isLengthValid });
    setPasswordReq2({
      ...passwordReq2,
      state: /^(?=.*[a-z])(?=.*[A-Z])/.test(passwordReg),
    });
    setPasswordReq3({
      ...passwordReq3,
      state: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>\/?]+/.test(passwordReg),
    });
    setPasswordReq4({ ...passwordReq4, state: hasNumbers });
    const passwordValid =
      hasUpperCase &&
      hasLowerCase &&
      hasNumbers &&
      hasSpecialChars &&
      isLengthValid;
    setError(!passwordValid);

    // If any validation fails, stop here (inline messages will show)
    if (!usernameValid || !emailValid || !passwordValid) {
      return;
    }

    setLoading(true);
    axios
      .post(`${process.env.REACT_APP_API_URL}/api/auth/local/register`, {
        username: UsernamedReg,
        email: email.value,
        password: passwordReg,
        rolename: "Pekta Baru",
      })
      .then(async (response) => {
        const jwt: string | undefined = response?.data?.jwt;
        setRegisteredJwt(jwt || null);

        // After successful registration, update user's role via PUT
        try {
          const userId: number | undefined = response?.data?.user?.id;
          if (jwt && userId) {
            await axios.put(
              `${process.env.REACT_APP_API_URL}/api/custom/${userId}/role`,
              { roleId: 8 },
              { headers: { Authorization: `Bearer ${jwt}` } }
            );
          }
        } catch (putError) {
          // Non-blocking: proceed to login even if PUT fails
          console.error(
            "Failed to update user role after registration:",
            putError
          );
        }

        try {
          await requestVerification(email.value, jwt);
        } catch (verificationError: any) {
          const message =
            verificationError?.response?.data?.error?.message ||
            verificationError?.message ||
            "Tidak dapat menghantar emel pengesahan.";
          toast.error(message, { position: toast.POSITION.TOP_CENTER });
        }

        setRegisteredEmail(email.value);
        toast.success(
          "Pendaftaran berjaya. Sila semak emel anda (inbox atau folder spam) untuk pengesahan.",
          {
            position: toast.POSITION.TOP_CENTER,
          }
        );
      })
      .catch((error) => {
        console.log("error", error);
        const errorMsg = error.response
          ? error.response.data.message
          : "An unexpected error occurred";

        if (error.response && error.response.status === 400) {
          toast.error(
            `Email atau username yang digunakan sudah didaftarkan. Sila log masuk ke akaun anda.`,
            { position: toast.POSITION.TOP_CENTER }
          );
        } else {
          toast.error(errorMsg, { position: toast.POSITION.TOP_CENTER });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleResendVerification = async () => {
    if (!registeredEmail) return;

    try {
      await requestVerification(registeredEmail, registeredJwt);
      toast.success(
        "Pautan pengesahan emel telah dihantar semula. Sila semak inbox atau folder spam.",
        {
          position: toast.POSITION.TOP_CENTER,
        }
      );
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.message ||
        "Gagal menghantar semula pautan pengesahan.";
      toast.error(errorMessage, { position: toast.POSITION.TOP_CENTER });
    }
  };

  const handleUsernameChange = (e: any) => {
    const newUsername = e.target.value;
    setUsername(newUsername);
    const isValid =
      /^[a-zA-Z0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>\/?]{4,18}$/.test(newUsername);
    setUsernameIsValid(isValid);
    setUsernameError(
      isValid ? "" : "Mestilah antara 4 hingga 18 karakter dan tiada ruang"
    );
  };
  const handlePasswordChange = (e: any) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setError(false);

    // Validate each requirement and update its state
    setPasswordReq1({ ...passwordReq1, state: newPassword.length >= 8 });
    setPasswordReq2({
      ...passwordReq2,
      state: /^(?=.*[a-z])(?=.*[A-Z])/.test(newPassword),
    });
    setPasswordReq3({
      ...passwordReq3,
      state: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>\/?]+/.test(newPassword),
    });
    setPasswordReq4({ ...passwordReq4, state: /\d/.test(newPassword) });
  };
  const handleEmailChange = (e: any) => {
    const val = e.target.value;
    const valid = validateEmail(val);
    setEmailIsValid(valid);
    setEmailError(valid ? "" : "Sila masukkan emel yang sah.");

    const atIndex = val.indexOf("@");
    const trimmed = val.trim();
    if (trimmed.length === 0) {
      setEmailSuggestions([]);
      setShowEmailSuggestions(false);
      return;
    }

    if (atIndex === -1) {
      const localPart = trimmed;
      const suggestions = POPULAR_EMAIL_DOMAINS.map((d) => `${localPart}@${d}`);
      setEmailSuggestions(suggestions.slice(0, 6));
      setShowEmailSuggestions(true);
      return;
    }

    const localPart = trimmed.slice(0, atIndex);
    const domainPart = trimmed.slice(atIndex + 1).toLowerCase();
    if (localPart.length === 0) {
      setEmailSuggestions([]);
      setShowEmailSuggestions(false);
      return;
    }

    const matching = POPULAR_EMAIL_DOMAINS.filter((d) =>
      d.toLowerCase().startsWith(domainPart)
    );

    if (domainPart.length > 0 && matching.length === 0) {
      setEmailSuggestions([]);
      setShowEmailSuggestions(false);
      return;
    }

    const base = (
      domainPart.length === 0 ? POPULAR_EMAIL_DOMAINS : matching
    ).map((d) => `${localPart}@${d}`);
    setEmailSuggestions(base.slice(0, 6));
    setShowEmailSuggestions(true);
  };

  const handleKeywordKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.code === "Enter") {
      handleRegistration();
    }
  };

  return (
    <>
      <div>
        <figure className="h-screen flex bg-[url('/src/asset/mecca3.webp')] bg-cover">
          <div className="w-full max-w-md m-auto bg-white rounded-lg border border-primaryBorder shadow-default py-6 px-1">
            <blockquote className="text-2xl font-medium text-center">
              <img src={ebimbingan} className="m-auto" alt="icon" />
            </blockquote>

            <div className="text-primary m-6">
              <div className="flex items-center mt-3 justify-center">
                <h1 className="text-2xl font-medium text-primary mt-4 mb-2">
                  Pendaftaran
                </h1>
              </div>
              <form>
                <br />
                <label className="text-left">Username:</label>
                <input
                  type={"text"}
                  value={UsernamedReg}
                  placeholder="Username:"
                  className={
                    "w-full p-2 text-primary border rounded-md outline-none text-sm transition duration-150 ease-in-out mb-4"
                  }
                  autoComplete="new-password"
                  onChange={handleUsernameChange}
                />

                {!usernameIsValid && (
                  <p style={{ color: "red", marginBottom: "16px" }}>
                    {usernameError}
                  </p>
                )}
                <label className="text-left">Emel:</label>
                <div className="relative">
                  <input
                    type={"email"}
                    value={email.value}
                    onFocus={() => {
                      if (email.value && email.value.length > 0) {
                        const evt = { target: { value: email.value } } as any;
                        handleEmailChange(evt);
                        setShowEmailSuggestions(true);
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => setShowEmailSuggestions(false), 150);
                    }}
                    onChange={(e) => {
                      email.onChange(e);
                      handleEmailChange(e);
                    }}
                    placeholder="Emel"
                    className={
                      "w-full p-2 text-primary border rounded-md outline-none text-sm transition duration-150 ease-in-out mb-4"
                    }
                    autoComplete="new-password"
                  />
                  {showEmailSuggestions && emailSuggestions.length > 0 && (
                    <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-sm max-h-48 overflow-auto">
                      {emailSuggestions.map((sug) => (
                        <li
                          key={sug}
                          className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm text-gray-700"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            email.setValue(sug);
                            const evt = { target: { value: sug } } as any;
                            handleEmailChange(evt);
                            setShowEmailSuggestions(false);
                          }}
                        >
                          {sug}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {!emailIsValid && (
                  <p
                    style={{
                      color: "red",
                      marginTop: "-8px",
                      marginBottom: "16px",
                    }}
                  >
                    {emailError}
                  </p>
                )}
                <label>Kata Laluan:</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={passwordReg}
                    placeholder="••••••••"
                    className="w-full h-11 pr-10 p-2 text-primary border rounded-md outline-none text-sm transition duration-150 ease-in-out mb-2"
                    onChange={handlePasswordChange}
                    style={{ borderColor: error ? "red" : "" }}
                    autoComplete="new-password"
                    onKeyPress={handleKeywordKeyPress}
                    aria-invalid={!!error}
                    aria-describedby="password-help"
                  />

                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-pressed={showPassword}
                    aria-controls="password"
                    title={showPassword ? "Sembunyikan kata laluan" : "Tunjukkan kata laluan"}
                    className="absolute inset-y-0 right-0 flex items-center justify-center w-10 pr-2 pb-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  >
                    {showPassword ? (
                      <EyeOffIcon className="w-5 h-5" aria-hidden="true" />
                    ) : (
                      <EyeIcon className="w-5 h-5" aria-hidden="true" />
                    )}
                  </button>
                </div>

                {!passwordReq1.state && (
                  <p style={{ color: "red" }}>{passwordReq1.error}</p>
                )}
                {!passwordReq2.state && (
                  <p style={{ color: "red" }}>{passwordReq2.error}</p>
                )}
                {!passwordReq3.state && (
                  <p style={{ color: "red" }}>{passwordReq3.error}</p>
                )}
                {!passwordReq4.state && (
                  <p style={{ color: "red" }}>{passwordReq4.error}</p>
                )}

                <div className="flex items-center mt-3 justify-center">
                  <input
                    className="bg-lime-700 hover:bg-green-800 py-2 px-4 text-md text-white rounded border border-blue focus:outline-none focus:border-black"
                    type="button"
                    value={loading ? "Sila tunggu sebentar..." : "Daftar"}
                    onClick={handleRegistration}
                    disabled={loading}
                  />
                </div>

                {registeredEmail && (
                  <div className="mt-4 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                    <p className="font-semibold">Pendaftaran berjaya.</p>
                    <p className="mt-1">
                      Kami telah menghantar emel pengesahan ke{" "}
                      <span className="font-semibold">{registeredEmail}</span>.
                      Sila semak inbox atau folder spam, kemudian klik pautan
                      dalam emel tersebut sebelum log masuk.
                    </p>
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        className="rounded bg-lime-700 px-3 py-1 text-white hover:bg-green-800"
                        onClick={() => navigate("/login")}
                      >
                        Ke Log Masuk
                      </button>
                      <button
                        type="button"
                        className="rounded border border-green-600 px-3 py-1 text-green-700 hover:bg-green-100"
                        onClick={handleResendVerification}
                      >
                        Hantar Semula Emel
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex items-center mt-3 justify-center">
                  <p className="mb-6 text-base font-normal text-gray-700">
                    Sudah mempunyai akaun?{" "}
                    <a href="/login" className="hover:underline">
                      Log masuk
                    </a>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </figure>
      </div>
    </>
  );
}

const useFormInput = (initialValue: any) => {
  const [value, setValue] = useState(initialValue);

  const handleChange = (e: any) => {
    setValue(e.target.value);
  };
  return {
    value,
    onChange: handleChange,
    setValue,
  };
};
